import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Box, Card, Stack, Typography, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import TouchAppIcon from '@mui/icons-material/TouchApp';

/** Troca entre os slides de fundo. */
const TROCA_MS = 7_000;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-14px); }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.12); }
`;

const ripple = keyframes`
  0%   { transform: scale(0.6); opacity: 0.55; }
  100% { transform: scale(2.4); opacity: 0; }
`;

export interface SlideProtecao {
  /** Chave estável — controla o cross-fade entre os slides. */
  chave: string;
  imagemUrl?: string;
}

/**
 * Fundo de fábrica de cada aparelho, usado quando a loja não subiu imagem
 * nenhuma. Existe para a tela do aparelho parado ser sempre a mesma coisa:
 * antes, quem não configurava caía na tela da marca — um layout inteiro
 * diferente (logo + cartão em degradê) em vez do fundo com a chamada, e a loja
 * ao lado, que tinha subido foto, mostrava outra coisa.
 *
 * Um por orientação, porque não é a mesma foto esticada: o totem fica EM PÉ
 * (retrato) e o tablet fica DEITADO na mesa (paisagem).
 */
export const BG_PADRAO_TOTEM = '/images/default-bg-images/totem_bg.jpg';
export const BG_PADRAO_TABLET = '/images/default-bg-images/tablet_mesa_bg.jpg';

/**
 * Monta os slides a partir das imagens configuradas, caindo no fundo de fábrica
 * quando não há nenhuma. A URL é a própria chave: repetir a mesma imagem não
 * faria sentido como slide, e assim o cross-fade não remonta à toa.
 */
export function slidesComPadrao(configuradas: string[], padrao: string): SlideProtecao[] {
  const urls = configuradas.length > 0 ? configuradas : [padrao];
  return urls.map((url) => ({ chave: url, imagemUrl: url }));
}

/**
 * Proteção de tela dos aparelhos da loja — totem de pedidos, tablet na mesa e
 * totem de checkout.
 *
 * Os três tinham a sua: o totem passava imagens com uma chamada em caixa alta, o
 * tablet passava produtos em destaque, e o totem de checkout tinha o carrossel
 * com o cartão de vidro e o toque pulsando. Três animações, três tipografias,
 * três jeitos de tratar a foto — na mesma loja, lado a lado. Hoje os três
 * passam as IMAGENS configuradas para o aparelho, e só.
 *
 * Ficou o desenho do totem de checkout, que era o mais trabalhado: fundo em
 * cross-fade com zoom lento, scrim para o texto ler em qualquer foto e cartão
 * central de vidro com o toque pulsando. O rodapé com a logo e o nome Sischef,
 * que existia no totem e no tablet, passou a valer para os três.
 *
 * A tela da marca (logo + cartão em degradê) continua aqui como rede de
 * segurança de quem não passar slide nenhum, mas os três aparelhos não caem
 * mais nela: usam {@link slidesComPadrao} e sempre têm fundo.
 */
export function ProtecaoTela({
  slides,
  chamadaAcima,
  chamadaPrincipal = 'para começar',
  ajuda,
  aoTocar,
  nivel = 'protecao',
}: {
  slides: SlideProtecao[];
  /** Linha menor, acima da principal (ex.: "Faça aqui o"). */
  chamadaAcima?: string;
  /** Linha grande da chamada (ex.: "Seu pedido!"). */
  chamadaPrincipal?: string;
  /** Texto discreto abaixo do cartão. */
  ajuda?: string;
  aoTocar?: () => void;
  /**
   * `protecao` (padrão) cobre tudo — é protetor de tela, qualquer toque dispensa.
   * `tela` é a tela inicial de um modo: fica NO nível do conteúdo, senão tampa o
   * botão "Sobre" (fixo, no z-index do fab) bem na hora em que o operador
   * precisa dele.
   */
  nivel?: 'protecao' | 'tela';
}): JSX.Element {
  const [indice, setIndice] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = window.setInterval(() => setIndice((i) => (i + 1) % slides.length), TROCA_MS);
    return () => window.clearInterval(id);
  }, [slides.length]);

  // Mantém o índice válido quando a lista muda (ex.: catálogo recarregou).
  useEffect(() => {
    if (indice >= slides.length) setIndice(0);
  }, [slides.length, indice]);

  const chamada = <Chamada acima={chamadaAcima} principal={chamadaPrincipal} />;

  return (
    <Box
      onClick={aoTocar}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (theme) => (nivel === 'protecao' ? theme.zIndex.modal + 100 : 0),
        overflow: 'hidden',
        cursor: aoTocar ? 'pointer' : 'default',
        bgcolor: '#0B1116',
      }}
    >
      {slides.length === 0 ? (
        <TelaDaMarca>{chamada}</TelaDaMarca>
      ) : (
        <>
          {slides.map((slide, i) => (
            <Box
              key={slide.chave}
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: slide.imagemUrl ? `url("${slide.imagemUrl}")` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: i === indice ? 1 : 0,
                transform: i === indice ? 'scale(1.07)' : 'scale(1)',
                transition: 'opacity 1.2s ease-in-out, transform 8s ease-out',
              }}
            />
          ))}

          {/* Scrim: garante leitura do texto sobre qualquer foto. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.55) 100%)',
            }}
          />

          <Stack sx={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', px: 3, gap: 4 }}>
            <Box
              sx={{
                px: { xs: 4, md: 8 },
                py: { xs: 5, md: 7 },
                borderRadius: 8,
                maxWidth: 'min(900px, 94vw)',
                bgcolor: 'rgba(0,0,0,0.38)',
                border: '1px solid rgba(255,255,255,0.18)',
                animation: `${fadeInUp} 0.9s ease-out both`,
              }}
            >
              {chamada}
            </Box>

            {ajuda && (
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {ajuda}
              </Typography>
            )}
          </Stack>

          {slides.length > 1 && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ position: 'absolute', left: 0, right: 0, bottom: 110, justifyContent: 'center' }}
            >
              {slides.map((slide, i) => (
                <Box
                  key={`ponto-${slide.chave}`}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: i === indice ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
                    transition: 'background-color 0.4s ease',
                  }}
                />
              ))}
            </Stack>
          )}
        </>
      )}

      <RodapeSischef />
    </Box>
  );
}

/** Toque pulsando + chamada. É o convite a encostar na tela. */
function Chamada({ acima, principal }: { acima?: string; principal: string }): JSX.Element {
  return (
    <Stack spacing={5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 80,
          height: 80,
        }}
      >
        {[0, 1].map((i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: 80,
              height: 80,
              borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.6)',
              animation: `${ripple} 2.2s ease-out ${i * 1.1}s infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        ))}

        <TouchAppIcon
          sx={{
            fontSize: 100,
            color: 'white',
            position: 'relative',
            animation: `${pulse} 1.8s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      </Box>

      <Typography
        variant="h3"
        color="white"
        sx={{ textAlign: 'center', fontWeight: 400, fontSize: { xs: '2.2rem', md: '3.5rem' }, textTransform: 'uppercase' }}
      >
        {acima ? (
          <>
            {acima} <br /> <strong>{principal}</strong>
          </>
        ) : (
          <>
            <strong>Toque</strong> <br /> {principal}
          </>
        )}
      </Typography>
    </Stack>
  );
}

/** Sem foto configurada: a tela vira a da marca, com o cartão em degradê subindo. */
function TelaDaMarca({ children }: { children: ReactNode }): JSX.Element {
  const theme = useTheme();
  const escuro = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Stack sx={{ alignItems: 'center', justifyContent: 'center', flexGrow: 1, px: 3 }}>
        <Box
          component="img"
          src={
            escuro
              ? '/images/sischef-logo/escrita-branca-vertical.svg'
              : '/images/sischef-logo/escrita-preta-vertical.svg'
          }
          alt="Sischef"
          sx={{
            maxWidth: 500,
            width: '100%',
            animation: `${fadeInUp} 0.9s ease-out both, ${floatY} 5s ease-in-out 0.9s infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      </Stack>

      <Card
        elevation={0}
        sx={{
          pt: 8,
          // Espaço para o rodapé da marca não encostar na chamada.
          pb: 14,
          px: 2,
          width: '96%',
          borderRadius: '48px 48px 0 0',
          background: escuro
            ? `linear-gradient(180deg, ${theme.palette.personal.header} 0%, ${theme.palette.personal.menu} 100%)`
            : `linear-gradient(180deg, ${theme.palette.primary.main} 0%, #F53B78 100%)`,
          animation: `${slideUp} 0.7s cubic-bezier(0.16, 1, 0.3, 1) both`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {children}
      </Card>
    </Box>
  );
}

/**
 * Assinatura da plataforma. Vinha do totem e do tablet; o totem de checkout não
 * tinha, e agora tem — é a mesma proteção de tela nos três.
 */
function RodapeSischef(): JSX.Element {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        py: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        // Vidro fosco escuro: o fundo continua por baixo, logo e nome em branco.
        bgcolor: 'rgba(0,0,0,0.32)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid rgba(255,255,255,0.15)',
      }}
    >
      <Box component="img" src="/logo-sischef-128x128.png" alt="" sx={{ height: { xs: 36, sm: 44 } }} />
      <Typography
        sx={{ color: '#ffffff', fontWeight: 800, letterSpacing: '0.02em', fontSize: { xs: '1.4rem', sm: '1.8rem' } }}
      >
        Sischef
      </Typography>
    </Box>
  );
}
